import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Save, X, Tag, Folder, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface PostType {
  id: number;
  client_id: number;
  name: string;
  category: string;
  description?: string;
  default_template?: string;
  is_active: boolean;
  usage_count?: number;
}

interface Taxonomy {
  id: number;
  post_type_id: number;
  name: string;
  slug: string;
  description?: string;
}

interface Client {
  id: number;
  name: string;
}

export default function PostTypesManager() {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number>(0);
  const [postTypes, setPostTypes] = useState<PostType[]>([]);
  const [selectedPostType, setSelectedPostType] = useState<PostType | null>(null);
  const [taxonomies, setTaxonomies] = useState<Taxonomy[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPostTypeId, setEditingPostTypeId] = useState<number | null>(null);
  const [editingTaxonomyId, setEditingTaxonomyId] = useState<number | null>(null);
  const [showCreatePostTypeModal, setShowCreatePostTypeModal] = useState(false);
  const [showCreateTaxonomyModal, setShowCreateTaxonomyModal] = useState(false);

  const [postTypeForm, setPostTypeForm] = useState({
    name: '',
    description: ''
  });

  const [taxonomyForm, setTaxonomyForm] = useState({
    name: '',
    slug: '',
    description: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      loadPostTypes();
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedPostType) {
      loadTaxonomies(selectedPostType.id);
    }
  }, [selectedPostType]);

  const loadClients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clients`);
      setClients(response.data);
      if (response.data.length > 0) {
        setSelectedClientId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPostTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/post-types`, {
        params: { client_id: selectedClientId }
      });
      setPostTypes(response.data);
      if (response.data.length > 0 && !selectedPostType) {
        setSelectedPostType(response.data[0]);
      } else if (response.data.length === 0) {
        setSelectedPostType(null);
      }
    } catch (error) {
      console.error('Failed to load post types:', error);
    }
  };

  const loadTaxonomies = async (postTypeId: number) => {
    try {
      // TODO: Replace with actual taxonomy API endpoint when backend is ready
      setTaxonomies([]);
    } catch (error) {
      console.error('Failed to load taxonomies:', error);
    }
  };

  const handleCreatePostType = async () => {
    try {
      if (!postTypeForm.name.trim()) {
        alert('Please enter a post type name');
        return;
      }

      await axios.post(`${API_BASE_URL}/post-types`, {
        name: postTypeForm.name,
        description: postTypeForm.description,
        category: 'custom',
        client_id: selectedClientId,
        is_active: true
      });

      setShowCreatePostTypeModal(false);
      resetPostTypeForm();
      await loadPostTypes();
    } catch (error: any) {
      console.error('Failed to create post type:', error);
      alert(`Failed to create post type: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleUpdatePostType = async (id: number) => {
    try {
      await axios.put(`${API_BASE_URL}/post-types/${id}`, {
        name: postTypeForm.name,
        description: postTypeForm.description,
        category: 'custom'
      });
      setEditingPostTypeId(null);
      resetPostTypeForm();
      await loadPostTypes();
    } catch (error: any) {
      console.error('Failed to update post type:', error);
      alert(`Failed to update post type: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeletePostType = async (id: number, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This will also delete all associated taxonomies.`)) return;

    try {
      const response = await axios.delete(`${API_BASE_URL}/post-types/${id}`);
      alert(response.data.message);
      if (selectedPostType?.id === id) {
        const remaining = postTypes.filter(pt => pt.id !== id);
        setSelectedPostType(remaining[0] || null);
      }
      await loadPostTypes();
    } catch (error: any) {
      console.error('Failed to delete post type:', error);
      alert(`Failed to delete post type: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCreateTaxonomy = async () => {
    if (!selectedPostType) return;

    try {
      if (!taxonomyForm.name.trim()) {
        alert('Please enter a taxonomy name');
        return;
      }

      // Auto-generate slug if empty
      const slug = taxonomyForm.slug || taxonomyForm.name.toLowerCase().replace(/\s+/g, '-');

      // TODO: Replace with actual taxonomy API endpoint when backend is ready
      alert('Taxonomy API not yet implemented. Coming soon!');

      setShowCreateTaxonomyModal(false);
      resetTaxonomyForm();
    } catch (error: any) {
      console.error('Failed to create taxonomy:', error);
      alert(`Failed to create taxonomy: ${error.response?.data?.error || error.message}`);
    }
  };

  const startEditPostType = (postType: PostType) => {
    setEditingPostTypeId(postType.id);
    setPostTypeForm({
      name: postType.name,
      description: postType.description || ''
    });
  };

  const cancelEditPostType = () => {
    setEditingPostTypeId(null);
    resetPostTypeForm();
  };

  const resetPostTypeForm = () => {
    setPostTypeForm({
      name: '',
      description: ''
    });
  };

  const resetTaxonomyForm = () => {
    setTaxonomyForm({
      name: '',
      slug: '',
      description: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Post Types & Taxonomies</h1>
        <p className="text-gray-600 mt-1">Manage post types and their taxonomies for content organization</p>
      </div>

      {/* Client Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Client
        </label>
        <select
          value={selectedClientId}
          onChange={(e) => {
            setSelectedClientId(parseInt(e.target.value));
            setSelectedPostType(null);
          }}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {clients.map(client => (
            <option key={client.id} value={client.id}>{client.name}</option>
          ))}
        </select>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex h-[calc(100vh-350px)]">
          {/* Left Sidebar - Post Types */}
          <div className="w-80 bg-gray-50 border-r border-gray-200 flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">
                  Post Types
                </h3>
                <button
                  onClick={() => setShowCreatePostTypeModal(true)}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 flex items-center gap-1"
                  title="Add Post Type"
                >
                  <Plus className="w-3 h-3" />
                  New
                </button>
              </div>
              <p className="text-xs text-gray-500">{postTypes.length} types</p>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-1">
                {postTypes.map(postType => (
                  <div key={postType.id} className="relative group">
                    {editingPostTypeId === postType.id ? (
                      <div className="bg-white border border-gray-300 rounded-lg p-2 space-y-2">
                        <input
                          type="text"
                          value={postTypeForm.name}
                          onChange={(e) => setPostTypeForm({...postTypeForm, name: e.target.value})}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="Post type name"
                        />
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleUpdatePostType(postType.id)}
                            className="flex-1 px-2 py-1 text-xs text-white bg-green-600 rounded hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditPostType}
                            className="flex-1 px-2 py-1 text-xs text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelectedPostType(postType)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                          selectedPostType?.id === postType.id
                            ? 'bg-blue-100 text-blue-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Tag className="w-4 h-4 flex-shrink-0" />
                          <div className="text-left min-w-0">
                            <div className="truncate font-medium">{postType.name}</div>
                            {postType.description && (
                              <div className="text-xs text-gray-500 truncate">{postType.description}</div>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 flex-shrink-0" />
                      </button>
                    )}
                    {!editingPostTypeId && (
                      <div className="absolute right-2 top-2 hidden group-hover:flex items-center gap-1 bg-white rounded shadow-sm border border-gray-200">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditPostType(postType);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePostType(postType.id, postType.name);
                          }}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {postTypes.length === 0 && (
                <div className="text-center py-12 px-4">
                  <Tag className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 mb-3">No post types yet</p>
                  <button
                    onClick={() => setShowCreatePostTypeModal(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Create your first post type
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Content - Taxonomies */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedPostType ? (
              <>
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Taxonomies for "{selectedPostType.name}"
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedPostType.description || 'Organize content with custom taxonomies'}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowCreateTaxonomyModal(true)}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      New Taxonomy
                    </button>
                  </div>
                </div>

                {/* Taxonomies Table */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Slug
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {taxonomies.map(taxonomy => (
                          <tr key={taxonomy.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <Folder className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">{taxonomy.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <code className="text-xs bg-gray-100 px-2 py-1 rounded">{taxonomy.slug}</code>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-600 truncate max-w-md">
                                {taxonomy.description || '-'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button className="text-blue-600 hover:text-blue-900">
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button className="text-red-600 hover:text-red-900">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {taxonomies.length === 0 && (
                      <div className="p-12 text-center">
                        <Folder className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500 mb-2">No taxonomies yet</p>
                        <p className="text-sm text-gray-400 mb-4">
                          Example: For "Team Members" you might add "Roles", "Departments", etc.
                        </p>
                        <button
                          onClick={() => setShowCreateTaxonomyModal(true)}
                          className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          Create first taxonomy
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">
                    {postTypes.length === 0 ? 'No post types created yet' : 'Select a post type to manage taxonomies'}
                  </p>
                  {postTypes.length === 0 && (
                    <button
                      onClick={() => setShowCreatePostTypeModal(true)}
                      className="mt-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      Create your first post type
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Post Type Modal */}
      {showCreatePostTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Post Type</h3>
              <button
                onClick={() => {
                  setShowCreatePostTypeModal(false);
                  resetPostTypeForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Post Type Name *
                </label>
                <input
                  type="text"
                  value={postTypeForm.name}
                  onChange={(e) => setPostTypeForm({...postTypeForm, name: e.target.value})}
                  placeholder="e.g., Team Members, Services, Service Areas"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Examples: Team Members, Services, Products</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={postTypeForm.description}
                  onChange={(e) => setPostTypeForm({...postTypeForm, description: e.target.value})}
                  placeholder="Brief description of this post type..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreatePostTypeModal(false);
                  resetPostTypeForm();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePostType}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Create Post Type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Taxonomy Modal */}
      {showCreateTaxonomyModal && selectedPostType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Taxonomy</h3>
              <button
                onClick={() => {
                  setShowCreateTaxonomyModal(false);
                  resetTaxonomyForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                Creating taxonomy for: <strong>{selectedPostType.name}</strong>
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Example: For "Team Members" → add "Roles", "Departments"
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taxonomy Name *
                </label>
                <input
                  type="text"
                  value={taxonomyForm.name}
                  onChange={(e) => setTaxonomyForm({...taxonomyForm, name: e.target.value})}
                  placeholder="e.g., Roles, Departments, Service Types"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slug (Optional)
                </label>
                <input
                  type="text"
                  value={taxonomyForm.slug}
                  onChange={(e) => setTaxonomyForm({...taxonomyForm, slug: e.target.value})}
                  placeholder="Auto-generated from name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">URL-friendly version (e.g., "roles")</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={taxonomyForm.description}
                  onChange={(e) => setTaxonomyForm({...taxonomyForm, description: e.target.value})}
                  placeholder="Brief description of this taxonomy..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateTaxonomyModal(false);
                  resetTaxonomyForm();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTaxonomy}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Create Taxonomy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}